import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import { Square3Stack3DIcon } from '@heroicons/react/24/outline'
import { orderBy } from 'lodash'
import { useHistory, useLocation } from 'react-router-dom'

import { getCurrentSpaceId, getCurrentUserRoles } from '@/components/App/selectors'
import useAnalytics from '@/hooks/useAnalytics'
import { changeCurrentSpace as changeCurrentSpaceAction } from '@/store/actions'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { getSpaces } from '@/store/selectors'
import clientLocalStorage from '@/utilities/clientLocalStorage'
import cn from '@/utilities/cn'

function SpaceSelect() {
  const analytics = useAnalytics()

  const history = useHistory()
  const location = useLocation()
  const dispatch = useAppDispatch()

  const spaces = useAppSelector(getSpaces)
  const userRoles = useAppSelector(getCurrentUserRoles)
  const currentSpaceId = useAppSelector(getCurrentSpaceId)

  const userSpaces = spaces.filter(space => space.id in (userRoles.spaces ?? {}))

  const changeCurrentSpace = async (newSpaceId: any) => {
    if (newSpaceId) {
      // need to change the path before updating redux
      if (location.pathname.includes('/space/')) {
        history.push(location.pathname.replace(currentSpaceId, newSpaceId))
      }

      await dispatch(changeCurrentSpaceAction(newSpaceId))
      analytics.track('space-change', { spaceName: spaces.find(space => space.id === newSpaceId)?.name })
    }
  }

  return (
    <Menu as="div" className="relative">
      <div>
        <MenuButton className="inline-flex items-center rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50">
          <span className="max-w-[10rem] truncate" title={spaces.find(space => space.id === currentSpaceId)?.name}>
            {spaces.find(space => space.id === currentSpaceId)?.name}
          </span>
          <Square3Stack3DIcon className="ml-2 h-4 w-4 flex-shrink-0" />
        </MenuButton>
      </div>
      <Transition
        enter="transition ease-out duration-200"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems className="absolute right-0 z-[99999] mt-2 w-48 min-w-min origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
          {orderBy(userSpaces, ['name']).map(space => (
            <MenuItem key={space.id}>
              {({ focus }) => (
                <button
                  className={cn(
                    focus ? 'bg-gray-100' : '',
                    'block w-full whitespace-nowrap px-4 py-2 text-left text-sm text-gray-700'
                  )}
                  onClick={() => {
                    clientLocalStorage.setItem('selectedSpace', space.id)
                    changeCurrentSpace(space.id)
                  }}
                >
                  {space.name}
                </button>
              )}
            </MenuItem>
          ))}
        </MenuItems>
      </Transition>
    </Menu>
  )
}

export default SpaceSelect
